import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { COACHES } from '../lib/coaches';
import { Footer } from '../components/Footer';
import { Hero } from '../components/sections/Hero';
import { Navigation } from '../components/Navigation';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { useLanguage } from '../context/LanguageContext';

const CoachPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const coach = COACHES.find(c => c.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!coach) {
    return (
      <div className="min-h-screen bg-black">
        <Navigation />
        <div className="pt-40 pb-24 px-6 max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-white mb-6">{t.coachPage.notFound}</h1>
          <Link to="/" className="text-sky-400 hover:text-white">← {t.coachPage.backHome}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const bio = t.coachBios?.[slug] || {};
  const bioParagraphs = bio.paragraphs || [t.coachPage.bioComingSoon];
  const highlights = bio.highlights || [];
  const languages = bio.languages || [];
  const whatsappBookingText = `Hallo! Ich möchte gerne ein Training mit ${coach.name} buchen.`;

  return (
    <div className="min-h-screen bg-white" data-testid={`coach-page-${slug}`}>
      <Navigation />

      {/* Hero with image + title */}
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-gradient-to-br from-blue-900 via-slate-900 to-sky-900"></div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm font-semibold mb-6 transition-colors"
            data-testid="coach-back-btn"
          >
            ← {t.coachPage.back}
          </button>

          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 md:gap-12 items-start">
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10 max-w-[280px] mx-auto md:mx-0 w-full">
              <img src={coach.image} alt={coach.name} className="w-full h-auto object-cover" />
            </div>

            <div className="text-center md:text-left">
              <p className="overline text-sky-400 mb-3">{t.trainers[coach.titleKey]}</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight">
                {coach.name}
              </h1>

              <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-6">
                <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-semibold text-white">
                  {t.trainers[coach.experienceKey]}
                </span>
                <span className="px-3 py-1.5 rounded-full bg-sky-500/20 border border-sky-400/40 text-xs font-semibold text-sky-300">
                  {t.trainers[coach.specialtyKey]}
                </span>
              </div>

              <WhatsAppButton
                text={whatsappBookingText}
                testId="coach-book-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#5B7FA8] hover:bg-[#4A6D95] text-white font-bold transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {t.coachPage.bookTraining}
              </WhatsAppButton>
            </div>
          </div>
        </div>
      </section>

      {/* Bio body */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{t.coachPage.aboutHeading}</h2>
          <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-5">
            {bioParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {highlights.length > 0 && (
            <div className="mt-12">
              <h3 className="text-lg font-bold text-slate-900 mb-4 uppercase tracking-wider">{t.coachPage.highlights}</h3>
              <ul className="space-y-2">
                {highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {languages.length > 0 && (
            <div className="mt-10 pt-6 border-t border-slate-200">
              <h3 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">{t.coachPage.languages}</h3>
              <p className="text-slate-700 font-medium">{languages.join(' · ')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Other coaches */}
      <section className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h3 className="text-xs font-bold text-slate-500 mb-5 uppercase tracking-wider">{t.coachPage.otherCoaches}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {COACHES.filter(c => c.slug !== slug).map(c => (
              <Link
                key={c.slug}
                to={`/coach/${c.slug}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200 hover:border-blue-500 transition-colors"
              >
                <img src={c.image} alt={c.name} className="w-12 h-12 rounded-full object-cover" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate">{c.name}</p>
                  <p className="text-xs text-slate-500 truncate">{t.trainers[c.titleKey]}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Production base URL for canonical / OG / sitemap

export default CoachPage;
